/**
 * Scope-filter compile/parse (#1867 PR2 — the visual builder ⇄ `filterJql`).
 *
 * ONE shared module owning both directions so the builder and the Advanced
 * (raw JQL) view can never drift. It produces / consumes the FILTER FRAGMENT
 * ONLY — the backend `buildJql` owns the `project` / `issuetype` / `updated`
 * clauses and the `ORDER BY`, and wraps this fragment in parens. So compile
 * MUST NOT emit any of those, and parse only ever sees the fragment.
 *
 * Contract (the invariant this module is tested against):
 *   - `compile` runs ONLY on builder conditions, never on an Advanced string.
 *   - `parse` recognizes exactly the grammar `compile` emits (plus a few
 *     lenient hand-edits); ANYTHING it can't model — nested groups, mixed
 *     AND/OR, an unknown field, `ORDER BY` — returns `{ mode: 'advanced', jql }`
 *     with the ORIGINAL string byte-preserved. The card then renders that raw
 *     string and never re-compiles it, so there is no lossy round-trip.
 *
 * Registry-based so PR2b (labels / assignee / reporter / created / updated)
 * adds a field by extending `FIELD_CONFIGS` + `FIELDS`, not by rewriting the
 * compiler. PR2a registers the three no-async fields.
 */

export type MatchMode = 'all' | 'any' // all → AND, any → OR

/** The fields the builder supports. PR2a: statusCategory/fixVersion/priority.
 * PR2b adds labels, assignee/reporter (people), and created/updated (dates). */
export type FieldKey =
  | 'statusCategory'
  | 'fixVersion'
  | 'priority'
  | 'labels'
  | 'assignee'
  | 'reporter'
  | 'created'
  | 'updated'

/** Operators. Set operators (`isAnyOf`/`isNotAnyOf`/`isEmpty`) drive the
 * string-set fields; the comparison operators (`inLast`/`before`/`after`)
 * drive the date fields (#1867 PR2b). `isEmpty`/`inLast` take no free values
 * beyond, respectively, none and the single relative token. */
export type OperatorKey = 'isAnyOf' | 'isNotAnyOf' | 'isEmpty' | 'inLast' | 'before' | 'after'

/** How the builder renders a field's value editor (which picker/source). */
export type ValueKind = 'statusCategory' | 'fixVersion' | 'priority' | 'labels' | 'user' | 'date'

export interface Condition {
  field: FieldKey
  op: OperatorKey
  /** The condition's value(s). Uniform `string[]` across field types:
   *   - set fields hold the human NAMES (workflow stage / version / priority /
   *     label), or, for people, the opaque `accountId`s;
   *   - date fields hold a SINGLE token in `values[0]` — a relative
   *     `-Nd`/`-Nw` (for `inLast`) or an ISO date `yyyy-MM-dd` (before/after).
   * Empty for `isEmpty`, or while a fresh condition is still incomplete. */
  values: string[]
}

export interface BuilderModel {
  mode: 'builder'
  match: MatchMode
  conditions: Condition[]
}
export interface AdvancedModel {
  mode: 'advanced'
  /** The raw JQL fragment, preserved verbatim. */
  jql: string
}
export type ScopeFilterModel = BuilderModel | AdvancedModel

interface FieldConfig {
  key: FieldKey
  jqlField: string
  label: string
  valueKind: ValueKind
  /** Operators this field offers — set operators for string-set fields,
   * comparison operators for date fields (#1867 PR2b). */
  operators: OperatorKey[]
}

const SET_OPS: OperatorKey[] = ['isAnyOf', 'isNotAnyOf', 'isEmpty']
const DATE_OPS: OperatorKey[] = ['inLast', 'before', 'after']

const FIELD_CONFIGS: Record<FieldKey, FieldConfig> = {
  statusCategory: { key: 'statusCategory', jqlField: 'statusCategory', label: 'Workflow stage', valueKind: 'statusCategory', operators: SET_OPS },
  fixVersion: { key: 'fixVersion', jqlField: 'fixVersion', label: 'Fix version', valueKind: 'fixVersion', operators: SET_OPS },
  priority: { key: 'priority', jqlField: 'priority', label: 'Priority', valueKind: 'priority', operators: SET_OPS },
  labels: { key: 'labels', jqlField: 'labels', label: 'Labels', valueKind: 'labels', operators: SET_OPS },
  assignee: { key: 'assignee', jqlField: 'assignee', label: 'Assignee', valueKind: 'user', operators: SET_OPS },
  reporter: { key: 'reporter', jqlField: 'reporter', label: 'Reporter', valueKind: 'user', operators: SET_OPS },
  created: { key: 'created', jqlField: 'created', label: 'Created', valueKind: 'date', operators: DATE_OPS },
  updated: { key: 'updated', jqlField: 'updated', label: 'Updated', valueKind: 'date', operators: DATE_OPS },
}

/** Field list for the builder's field dropdown (ordered). */
export const FIELDS: ReadonlyArray<FieldConfig> = [
  FIELD_CONFIGS.statusCategory,
  FIELD_CONFIGS.labels,
  FIELD_CONFIGS.fixVersion,
  FIELD_CONFIGS.priority,
  FIELD_CONFIGS.assignee,
  FIELD_CONFIGS.reporter,
  FIELD_CONFIGS.created,
  FIELD_CONFIGS.updated,
]

/** The fixed workflow-stage values — Jira's status categories, no discovery. */
export const STATUS_CATEGORY_VALUES = ['To Do', 'In Progress', 'Done'] as const

const OPERATOR_LABELS: Record<OperatorKey, string> = {
  isAnyOf: 'is any of',
  isNotAnyOf: 'is not any of',
  isEmpty: 'is empty',
  inLast: 'in the last',
  before: 'before',
  after: 'after',
}

/** The operators a given field offers, with UI labels. `isEmpty` reads
 * "is unassigned" for people fields, "is empty" elsewhere. */
export function operatorsForField(field: FieldKey): ReadonlyArray<{ key: OperatorKey; label: string }> {
  const cfg = FIELD_CONFIGS[field]
  return cfg.operators.map((k) => ({
    key: k,
    label: k === 'isEmpty' && cfg.valueKind === 'user' ? 'is unassigned' : OPERATOR_LABELS[k],
  }))
}

/** jqlField (lowercased) → FieldKey, for parse. */
const JQL_FIELD_TO_KEY: Record<string, FieldKey> = Object.fromEntries(
  Object.values(FIELD_CONFIGS).map((c) => [c.jqlField.toLowerCase(), c.key]),
) as Record<string, FieldKey>

/** A condition is ready to compile/save when it needs no values (`isEmpty`)
 * or has at least one non-blank value. */
export function isConditionComplete(c: Condition): boolean {
  return c.op === 'isEmpty' || c.values.some((v) => v.trim().length > 0)
}

export function fieldConfig(key: FieldKey): FieldConfig {
  return FIELD_CONFIGS[key]
}

// ── compile ────────────────────────────────────────────────────────────────

function quoteValue(v: string): string {
  return '"' + v.replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"'
}

/**
 * Compile a model to the JQL filter fragment (no project/type/updated, no
 * ORDER BY). Advanced mode returns its raw JQL untouched. Builder mode joins
 * complete conditions with AND (match all) or OR (match any); an incomplete
 * condition (an operator that needs values but has none) is skipped, so a
 * live preview still works while the operator is mid-edit (the card blocks
 * Save separately via `isConditionComplete`).
 */
/** A single relative-date token the builder emits, e.g. `-30d` / `-2w`. */
const RELATIVE_TOKEN = /^-\d+[dw]$/

/** Compile one condition to a JQL clause, or null when incomplete/invalid
 * (skipped so a live preview still works mid-edit). */
function compileCondition(c: Condition): string | null {
  const cfg = FIELD_CONFIGS[c.field]
  if (!cfg) return null
  const f = cfg.jqlField
  switch (c.op) {
    case 'isEmpty':
      return `${f} is EMPTY`
    case 'isAnyOf':
    case 'isNotAnyOf': {
      const vals = c.values.map((v) => v.trim()).filter((v) => v.length > 0)
      if (vals.length === 0) return null
      return `${f} ${c.op === 'isNotAnyOf' ? 'not in' : 'in'} (${vals.map(quoteValue).join(', ')})`
    }
    case 'inLast': {
      // Relative token, emitted UNQUOTED (Jira's relative dates aren't quoted).
      const token = (c.values[0] ?? '').trim()
      return RELATIVE_TOKEN.test(token) ? `${f} >= ${token}` : null
    }
    case 'before':
    case 'after': {
      const date = (c.values[0] ?? '').trim()
      return date ? `${f} ${c.op === 'before' ? '<' : '>'} ${quoteValue(date)}` : null
    }
  }
}

export function compile(model: ScopeFilterModel): string {
  if (model.mode === 'advanced') return model.jql.trim()
  const clauses = model.conditions.map(compileCondition).filter((c): c is string => c !== null)
  if (clauses.length === 0) return ''
  return clauses.join(model.match === 'any' ? ' OR ' : ' AND ')
}

// ── parse ────────────────────────────────────────────────────────────────

/** Split on top-level ` AND ` / ` OR ` (respecting quotes + parens). Returns
 * the single joiner used (or `null` for one clause), or `'mixed'` when both
 * appear at the top level — which the flat builder can't represent. */
function splitTopLevel(
  s: string,
): { joiner: 'AND' | 'OR' | null; parts: string[] } | 'mixed' {
  const parts: string[] = []
  let depth = 0
  let inQuote = false
  let cur = ''
  let joiner: 'AND' | 'OR' | null = null
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]
    if (inQuote) {
      if (ch === '\\' && i + 1 < s.length) {
        cur += ch + s[i + 1]
        i++
        continue
      }
      cur += ch
      if (ch === '"') inQuote = false
      continue
    }
    if (ch === '"') {
      inQuote = true
      cur += ch
      continue
    }
    if (ch === '(') {
      depth++
      cur += ch
      continue
    }
    if (ch === ')') {
      depth--
      cur += ch
      continue
    }
    if (depth === 0) {
      const rest = s.slice(i)
      const andM = /^\s+AND\s+/i.exec(rest)
      const orM = /^\s+OR\s+/i.exec(rest)
      if (andM) {
        if (joiner === 'OR') return 'mixed'
        joiner = 'AND'
        parts.push(cur)
        cur = ''
        i += andM[0].length - 1
        continue
      }
      if (orM) {
        if (joiner === 'AND') return 'mixed'
        joiner = 'OR'
        parts.push(cur)
        cur = ''
        i += orM[0].length - 1
        continue
      }
    }
    cur += ch
  }
  if (inQuote || depth !== 0) return 'mixed' // malformed → bail to Advanced
  parts.push(cur)
  return { joiner, parts }
}

/** Split a `(...)` value list on top-level commas, unquoting. Accepts quoted
 * (compile's output) and bare single-word values (lenient hand-edits). */
function parseValueList(inner: string): string[] | null {
  const out: string[] = []
  let cur = ''
  let inQuote = false
  for (let i = 0; i < inner.length; i++) {
    const ch = inner[i]
    if (inQuote) {
      if (ch === '\\' && i + 1 < inner.length) {
        cur += inner[i + 1]
        i++
        continue
      }
      if (ch === '"') {
        inQuote = false
        continue
      }
      cur += ch
      continue
    }
    if (ch === '"') {
      inQuote = true
      continue
    }
    if (ch === ',') {
      out.push(cur.trim())
      cur = ''
      continue
    }
    cur += ch
  }
  if (inQuote) return null
  out.push(cur.trim())
  const vals = out.map((v) => v.trim()).filter((v) => v.length > 0)
  return vals.length > 0 ? vals : null
}

/** Recognize one clause. Two grammars: the SET grammar (`field in (...)` /
 * `not in` / `is EMPTY`) for string-set fields, and the COMPARISON grammar
 * (`field >= -Nd` / `< "date"` / `> "date"`) for date fields. Returns null
 * (→ the whole filter falls to Advanced) for anything else. */
function parseClause(clause: string): Condition | null {
  // Set grammar — string-set fields only (date fields use comparison below).
  const setM = /^\s*([A-Za-z]+)\s+(not\s+in|in|is)\s+(.+?)\s*$/i.exec(clause)
  if (setM) {
    const key = JQL_FIELD_TO_KEY[setM[1].toLowerCase()]
    if (!key || FIELD_CONFIGS[key].valueKind === 'date') return null
    const op = setM[2].toLowerCase().replace(/\s+/g, ' ')
    const rhs = setM[3].trim()
    if (op === 'is') {
      return /^empty$/i.test(rhs) ? { field: key, op: 'isEmpty', values: [] } : null
    }
    const listM = /^\(\s*([\s\S]*?)\s*\)$/.exec(rhs)
    if (!listM) return null
    const values = parseValueList(listM[1])
    if (!values) return null
    return { field: key, op: op === 'not in' ? 'isNotAnyOf' : 'isAnyOf', values }
  }
  // Comparison grammar — date fields only.
  const cmpM = /^\s*([A-Za-z]+)\s+(>=|<|>)\s+(.+?)\s*$/.exec(clause)
  if (cmpM) {
    const key = JQL_FIELD_TO_KEY[cmpM[1].toLowerCase()]
    if (!key || FIELD_CONFIGS[key].valueKind !== 'date') return null
    const sym = cmpM[2]
    const rhs = cmpM[3].trim()
    if (sym === '>=') {
      // Only the relative "in the last N" shape round-trips; e.g.
      // `>= startOfMonth(-1)` can't be shown as a rule → Advanced.
      return RELATIVE_TOKEN.test(rhs) ? { field: key, op: 'inLast', values: [rhs] } : null
    }
    const dm = /^"([^"]*)"$/.exec(rhs)
    const date = dm ? dm[1] : rhs
    return date ? { field: key, op: sym === '<' ? 'before' : 'after', values: [date] } : null
  }
  return null
}

/**
 * Parse a stored `filterJql` fragment into the builder model, or fall back to
 * Advanced (raw JQL, byte-preserved) for anything the flat builder can't
 * represent. Empty ⇒ an empty builder (today's all-mapped-types scope).
 */
export function parse(filterJql: string | undefined | null): ScopeFilterModel {
  const jql = (filterJql ?? '').trim()
  if (jql === '') return { mode: 'builder', match: 'all', conditions: [] }
  if (/\border\s+by\b/i.test(jql)) return { mode: 'advanced', jql }
  const split = splitTopLevel(jql)
  if (split === 'mixed') return { mode: 'advanced', jql }
  const conditions: Condition[] = []
  for (const part of split.parts) {
    const c = parseClause(part)
    if (!c) return { mode: 'advanced', jql }
    conditions.push(c)
  }
  return { mode: 'builder', match: split.joiner === 'OR' ? 'any' : 'all', conditions }
}
